import React, { useEffect, useState, useCallback, Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import usePerformanceMetrics from "../hooks/usePerformanceMetrics";
import SEOHead from "../components/common/SEOHead";
import { homeSEO } from "../data/seoData";
import categoryData from "../data/categoryData";

const HeroSection = lazy(() => import("../components/home/HeroSection"));
const LuxuryShowcase = lazy(() => import("../components/home/LuxuryShowcase"));
const FeaturesSection = lazy(() => import("../components/home/FeaturesSection"));
const FeaturedProducts = lazy(() => import("../components/home/FeaturedProducts"));
const NewArrivals = lazy(() => import("../components/home/NewArrivals"));
const StatsSection = lazy(() => import("../components/home/StatsSection"));
const TestimonialsSection = lazy(() => import("../components/home/TestimonialsSection"));
const CTASection = lazy(() => import("../components/home/CTASection"));
const FloatingWhatsApp = lazy(() => import("../components/home/FloatingWhatsApp"));

const LoadingSpinner = ({ text = "Loading..." }) => (
  <div className="h-64 flex items-center justify-center">
    <div className="text-gray-500">{text}</div>
  </div>
);

const normalizeSearch = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const resolveCategorySlug = (rawCategory) => {
  const normalized = normalizeSearch(rawCategory).replace(/\s+/g, "");
  if (!normalized) return null;
  if (categoryData[normalized]) return normalized;

  for (const [slug, data] of Object.entries(categoryData)) {
    if (normalizeSearch(slug).replace(/\s+/g, "") === normalized) return slug;
    if (normalizeSearch(data?.title).replace(/\s+/g, "") === normalized) return slug;
  }
  return null;
};

const HomeSearch = ({
  query,
  setQuery,
  categoryResults,
  itemResults,
  searching,
  onClear,
  onCategoryClick,
  onItemClick
}) => (
  <section className="px-6 -mt-10 md:-mt-12 relative z-20">
    <div className="max-w-4xl mx-auto">
      <div className="bg-gray-900/85 backdrop-blur border border-gray-700 rounded-2xl p-3 md:p-4 shadow-2xl">
        <div className="flex items-center gap-2 md:gap-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products and categories..."
            className="w-full rounded-xl bg-white border border-gray-300 px-4 py-3 text-black placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          {query && (
            <button
              type="button"
              onClick={onClear}
              className="px-3 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 text-sm"
            >
              Clear
            </button>
          )}
        </div>

        {query && (
          <div className="mt-3 px-1">
            {searching ? (
              <p className="text-sm text-gray-400">Searching...</p>
            ) : (
              <p className="text-sm text-gray-400">
                {categoryResults.length + itemResults.length} results
              </p>
            )}
          </div>
        )}

        {query && !searching && (
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3 bg-gray-800 rounded-xl p-3">
            <div className="rounded-lg p-2">
              <h3 className="text-sm uppercase tracking-wide text-gray-400 mb-2">Categories</h3>
              {categoryResults.length === 0 ? (
                <p className="text-sm text-gray-400">No category match.</p>
              ) : (
                <div className="space-y-2">
                  {categoryResults.map((result) => (
                    <Link
                      key={result.slug}
                      to={`/category/${result.slug}`}
                      onClick={() => onCategoryClick(result)}
                      className="block p-3 rounded-md bg-gray-700/70 hover:bg-gray-700 transition-colors"
                    >
                      <p className="font-medium text-white">{result.title}</p>
                      <p className="text-xs text-gray-300 mt-0.5 line-clamp-2">{result.description}</p>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg p-2">
              <h3 className="text-sm uppercase tracking-wide text-gray-400 mb-2">Products</h3>
              {itemResults.length === 0 ? (
                <p className="text-sm text-gray-400">No product match.</p>
              ) : (
                <div className="space-y-2">
                  {itemResults.map((item) => (
                    <Link
                      key={item.id}
                      to={`/product/${item.id}`}
                      onClick={() => onItemClick(item)}
                      className="flex items-center gap-3 p-3 rounded-md bg-gray-700/70 hover:bg-gray-700 transition-colors"
                    >
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="w-12 h-12 rounded object-cover"
                      />
                      <div>
                        <p className="font-medium text-white">{item.title}</p>
                        <p className="text-xs text-gray-300">{item.categoryLabel}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  </section>
);

export default function Home() {
  usePerformanceMetrics();
  
  const [counters, setCounters] = useState({ years: 0, clients: 0, projects: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [uploadedItems, setUploadedItems] = useState([]);
  const [lastTrackedQuery, setLastTrackedQuery] = useState("");
  
  const updateCounters = useCallback(() => {
    let years = 0, clients = 0, projects = 0;
    const interval = setInterval(() => {
      years = Math.min(years + 1, 20);
      clients = Math.min(clients + 10, 500);
      projects = Math.min(projects + 20, 1000);
      setCounters({ years, clients, projects });
      if (years === 20 && clients === 500 && projects === 1000) clearInterval(interval);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const cleanup = updateCounters();
    return cleanup;
  }, [updateCounters]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 250);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  useEffect(() => {
    let active = true;
    const loadItems = async () => {
      try {
        const response = await fetch(`/api/items?t=${Date.now()}`, { cache: "no-store" });
        const payload = await response.json();
        if (active) setUploadedItems(Array.isArray(payload.items) ? payload.items : []);
      } catch {
        if (active) setUploadedItems([]);
      }
    };
    loadItems();
    return () => {
      active = false;
    };
  }, []);

  const { categoryResults, itemResults } = React.useMemo(() => {
    if (!debouncedSearchQuery) {
      return { categoryResults: [], itemResults: [] };
    }
    const query = normalizeSearch(debouncedSearchQuery);

    const rankedCategories = Object.entries(categoryData)
      .map(([slug, data]) => {
        const title = data.title || slug;
        const description = data.description || "";
        const titleNorm = normalizeSearch(title);
        const descNorm = normalizeSearch(description);
        let score = 0;
        if (titleNorm.startsWith(query)) score += 4;
        if (titleNorm.includes(query)) score += 2;
        if (descNorm.includes(query)) score += 1;
        return score > 0 ? { slug, title, description, score } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);

    const rankedItems = uploadedItems
      .map((item) => {
        const titleNorm = normalizeSearch(item.title);
        const descNorm = normalizeSearch(item.description);
        const catNorm = normalizeSearch(item.category);
        let score = 0;
        if (titleNorm.startsWith(query)) score += 4;
        if (titleNorm.includes(query)) score += 2;
        if (descNorm.includes(query)) score += 1;
        if (catNorm.includes(query)) score += 1;
        if (score === 0) return null;
        const slug = resolveCategorySlug(item.category);
        return {
          ...item,
          score,
          slug,
          categoryLabel: categoryData[slug]?.title || item.category
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);

    return { categoryResults: rankedCategories, itemResults: rankedItems };
  }, [debouncedSearchQuery, uploadedItems]);

  useEffect(() => {
    const query = debouncedSearchQuery.trim();
    if (!query || query === lastTrackedQuery) return;
    try {
      window?.performanceMonitor?.trackCustomEvent("home_search_performed", {
        query,
        categoryResults: categoryResults.length,
        itemResults: itemResults.length
      });
    } catch {
      // no-op
    }
    setLastTrackedQuery(query);
  }, [debouncedSearchQuery, categoryResults.length, itemResults.length, lastTrackedQuery]);

  return (
    <>
      <SEOHead {...homeSEO} />
      
      <div className="bg-gray-900 text-gray-100 font-sans">
      <Suspense fallback={<LoadingSpinner text="Loading hero section..." />}>
        <HeroSection />
      </Suspense>

      <HomeSearch
        query={searchQuery}
        setQuery={setSearchQuery}
        categoryResults={categoryResults}
        itemResults={itemResults}
        searching={Boolean(searchQuery.trim()) && debouncedSearchQuery !== searchQuery.trim()}
        onCategoryClick={(result) => {
          try {
            window?.performanceMonitor?.trackCustomEvent("home_search_category_click", {
              slug: result.slug,
              title: result.title
            });
          } catch {
            // no-op
          }
        }}
        onItemClick={(item) => {
          try {
            window?.performanceMonitor?.trackCustomEvent("home_search_item_click", {
              id: item.id,
              title: item.title,
              slug: item.slug || null
            });
          } catch {
            // no-op
          }
        }}
        onClear={() => {
          setSearchQuery("");
          setDebouncedSearchQuery("");
        }}
      />
      
      <Suspense fallback={<LoadingSpinner text="Loading showcase..." />}>
        <LuxuryShowcase />
      </Suspense>
      
      <Suspense fallback={<LoadingSpinner text="Loading features..." />}>
        <FeaturesSection />
      </Suspense>
      
      <Suspense fallback={<LoadingSpinner text="Loading products..." />}>
        <FeaturedProducts />
      </Suspense>

      <Suspense fallback={<LoadingSpinner text="Loading new arrivals..." />}>
        <NewArrivals />
      </Suspense>
      
      <Suspense fallback={<LoadingSpinner text="Loading statistics..." />}>
        <StatsSection counters={counters} />
      </Suspense>
      
      <Suspense fallback={<LoadingSpinner text="Loading testimonials..." />}>
        <TestimonialsSection />
      </Suspense>
      
      <Suspense fallback={<LoadingSpinner text="Loading call-to-action..." />}>
        <CTASection />
      </Suspense>
      
      <Suspense fallback={null}>
        <FloatingWhatsApp />
      </Suspense>
      </div>
    </>
  );
}
